// openNotificationTarget — the ONE "what does clicking this notification DO"
// decision (2026-08-06 per Nika "notifications should open in pageslot - also
// tickets, events and such features should be opened in their modals").
//
// A notification that references an entity with a FEATURE MODAL opens that
// modal IN PLACE — the current route never changes:
//   ticket → TicketsDetailModal (via the workspace shell's global
//            openTicketDetailModal, a @symbo.ls/windows layer mounted at
//            shell scope — opens over ANY route, not just /tickets)
//   event  → CalEventDetailModal (via openEventDetailModal, same layer)
// Everything else keeps the shared openInSlot decision (shell PageSlot when
// the route has a slot def, full navigation otherwise) so a click never
// silently does nothing.
//
// It does NOT call the modal openers directly: pageContext.js's
// `selectPageItem(kind, id)` already owns "resolve this entity id from any
// route and open its canonical surface" — loaded-row lookup, SDK fetch
// fallback for tickets, and the /calendar?event= handoff for events outside
// the loaded window (which fetches + maps + opens the SAME modal there).
// Reusing it keeps one open path per entity instead of a fourth copy of the
// fetch-and-normalize logic.
//
// Lives HERE, beside openInSlot.js and for the same reason (see that file's
// header): canvas's standalone bundle links shared, never workspace, and
// NotificationRow + the toast stack render there too. selectPageItem is a
// WORKSPACE-SHELL function, so this dispatcher feature-tests
// `context.functions.selectPageItem` and degrades to openInSlot wherever the
// shell layer isn't merged (canvas / marketplace / preview standalone).
//
// Entity resolution reads BOTH the row's `metadata` (server ticket
// notifications carry `metadata.ticketObjectId` — see
// server/src/domains/tickets/services/ticketNotifications.js `_emit`;
// calendar invites carry `metadata.eventId` — calendar/functions/saveEvent.js)
// AND the href (`/ticket/<id>` path, legacy `?ticket=` / `?event=` params), so
// pre-metadata rows already in the inbox keep working.
//
// CROSS-ORG hrefs (`/w/<other-org>/…` — e.g. QA tickets from the system org
// arriving in your inbox) are the ONE input the in-place machinery cannot
// serve: selectPageItem is current-org scoped (loaded rows + the org-scoped
// tickets/docs SDK services) and a bare `el.router` into a foreign `/w/` URL
// strands the tab — the ws-handle resolver only knows the ACTIVE org's
// workspaces, so the route classifies as Not-found under the CURRENT org's
// chrome (tickets/workspace.md "cross-org ticket/event targets land on
// 'Not found'"). Those divert to a FULL page load of the href before any
// in-place attempt — see _hardLoadCrossOrgHref for why that is the correct
// org transition. Same-org hrefs never enter that branch.
import { getWorkspacePrefixSlug, stripWorkspacePrefix } from './workspacePrefix.js'
import { openInSlot } from './openInSlot.js'

// FA204 / frank trap — _str/_decode used to live at module scope, referenced
// bare across every export below. Each export now carries its own
// self-contained inline copy. resolveNotificationTarget/
// isCrossOrgNotificationHref stay exported (test-only consumers,
// notificationScope.test.js/openNotificationTarget.test.js import them
// directly — not barreled, zero el.call consumers per functions/index.js's
// own comment); openNotificationTarget (the real, barreled, el.call target)
// gets its own self-contained duplicate of everything it needs instead of a
// bare sibling-export call.

// Pure `{ href, type, metadata }` → `{ kind, id }` (or null) resolution,
// exported for tests. `kind` values match pageContext.js selectPageItem's
// cases. An id is only ever read from a source that itself names the entity
// (a `/ticket/` path, a `?ticket=`/`?event=` param, a metadata id field), so
// no separate type check is needed — `type` rides along purely for callers'
// future use and diagnostics.
// @testOnly — exported for openNotificationTarget.test.js; no live code imports or calls it.
export const resolveNotificationTarget = (notif) => {
  const _str = (v) => (v == null ? '' : String(v))
  const _decode = (v) => {
    try {
      return decodeURIComponent(v)
    } catch (_) {
      return v
    }
  }
  if (!notif || typeof notif !== 'object') return null
  const href = _str(notif.href)
  const meta = notif.metadata && typeof notif.metadata === 'object' ? notif.metadata : {}
  const clean = stripWorkspacePrefix(href.split('?')[0].split('#')[0])

  // Ticket — server rows: href '/ticket/<mongoId>' (+ optional /w/<org>/<ws>
  // prefix), metadata.ticketObjectId + metadata.ticketId (human key). Legacy
  // client rows: '/tickets/…?ticket=<number|id>'. sdk.tickets.get accepts
  // both id forms (see tickets/pages/view.js).
  const ticketPath = clean.match(/^\/ticket\/([^/]+)$/)
  const ticketParam = href.match(/[?&]ticket=([^&#]+)/)
  const ticketId =
    _str(meta.ticketObjectId) ||
    (ticketPath ? _decode(ticketPath[1]) : '') ||
    (ticketParam ? _decode(ticketParam[1]) : '')
  if (ticketId) return { kind: 'ticket', id: ticketId }

  // Event — calendar invites use metadata.eventId; meeting-start notifications
  // use metadata.calendarEventId. Both keep a query-free '/calendar' href so
  // the desktop-notification click path survives. Legacy deep links carry
  // '?event=<id>' or the calendar's old '#event=<id>' hash.
  const eventParam = href.match(/[?&#]event=([^&#]+)/)
  const eventId =
    _str(meta.eventId) ||
    _str(meta.calendarEventId) ||
    (eventParam ? _decode(eventParam[1]) : '')
  if (eventId) return { kind: 'event', id: eventId }

  return null
}

// Pure cross-org test for a notification href, exported for tests. TRUE only
// when the href pins a `/w/<org>` prefix AND the active org is known AND the
// two name different orgs — an unknown/unresolved side never classifies as
// cross-org (fall through to the existing path rather than guess). The
// active-org slug read matches mwtRouter's `_orgSlug` / workspaceUrlSync's
// `stateSlug` (`slug` with `username` fallback) so this predicate can never
// disagree with the URL machinery about which org the session is in.
export const isCrossOrgNotificationHref = (href, root) => {
  const _str = (v) => (v == null ? '' : String(v))
  const hrefSlug = getWorkspacePrefixSlug(_str(href))
  if (!hrefSlug) return false
  const org = root && root.org
  const activeSlug = (org && (org.slug || org.username)) || null
  return !!activeSlug && hrefSlug !== activeSlug
}

// THE one sanctioned raw-location write in the notification click path — a
// FULL page load of a cross-org notification href.
//
// Why not SPA machinery: el.router keeps the CURRENT org's session, whose
// ws-handle resolver doesn't know the foreign org's workspaces — the route
// classifies as Not-found under the wrong chrome (the exact bug). The org
// switcher's commit (AppOrgSwitcher `_commitOrgSwitch`) is component-private,
// takes no destination, and even after `sdk.switchOrg` resolves, the foreign
// workspace list lands asynchronously via the onWorkspaceChange refetch —
// routing to the href before it lands re-renders the same Not-found until the
// mwtRouter heal re-dispatches. A cold load has no such gap: bootShell's
// URL-org scoped boot (BOOT-RESHUFFLE F1, workspace f8ac7143) resolves the
// org FROM the URL, fetches its workspaces and switches org pre-render, so
// the click lands exactly like a cold deep-link into that org — right chrome,
// right workspace scope, ticket/event page — which is this ticket's accept
// criterion verbatim.
//
// Precedent for hard-navigating a NOTIFICATION href specifically: the
// OS-notification click path already does it for hidden-tab clicks
// (workspace/functions/notifications/subscribeNotifications.js
// `_showBrowserNotification` assigns window.location). Raw location writes
// stay banned everywhere else.
//
// Window resolves `el.context.window`-first (mwtRouter's `_win` idiom) — also
// the tests' injection seam. Returns false when no window exists (SSR/tests)
// so the caller degrades to the existing SPA path instead of dead-ending.
// el.call('openNotificationTarget', { href, type, metadata }, el) — open the
// notification's target: its feature modal when it references one (current
// route preserved), the openInSlot slot-vs-navigate decision otherwise.
// Accepts a bare href string too (a caller with no row context loses nothing
// over openInSlot).
export const openNotificationTarget = async function (notif, el) {
  // FA204 / frank trap — self-contained inline copies of _str/
  // isCrossOrgNotificationHref/_hardLoadCrossOrgHref/resolveNotificationTarget
  // (a bare sibling-export call is exactly as unresolvable post-frank.toJSON
  // as a module-local read — the exports above stay live for their own real
  // consumers, the two test files named in the file header).
  const _str = (v) => (v == null ? '' : String(v))
  const _isCrossOrgNotificationHref = (href, root) => {
    const hrefSlug = getWorkspacePrefixSlug(_str(href))
    if (!hrefSlug) return false
    const org = root && root.org
    const activeSlug = (org && (org.slug || org.username)) || null
    return !!activeSlug && hrefSlug !== activeSlug
  }
  const _hardLoadCrossOrgHref = (el, href) => {
    const win = el?.context?.window || (typeof window !== 'undefined' ? window : null)
    if (!win || !win.location || typeof win.location.assign !== 'function') return false
    try {
      win.location.assign(href)
      return true
    } catch (_) {
      return false
    }
  }
  const _resolveNotificationTarget = (notif) => {
    const _decode = (v) => {
      try {
        return decodeURIComponent(v)
      } catch (_) {
        return v
      }
    }
    if (!notif || typeof notif !== 'object') return null
    const href = _str(notif.href)
    const meta = notif.metadata && typeof notif.metadata === 'object' ? notif.metadata : {}
    const clean = stripWorkspacePrefix(href.split('?')[0].split('#')[0])
    const ticketPath = clean.match(/^\/ticket\/([^/]+)$/)
    const ticketParam = href.match(/[?&]ticket=([^&#]+)/)
    const ticketId =
      _str(meta.ticketObjectId) ||
      (ticketPath ? _decode(ticketPath[1]) : '') ||
      (ticketParam ? _decode(ticketParam[1]) : '')
    if (ticketId) return { kind: 'ticket', id: ticketId }
    const eventParam = href.match(/[?&#]event=([^&#]+)/)
    const eventId =
      _str(meta.eventId) ||
      _str(meta.calendarEventId) ||
      (eventParam ? _decode(eventParam[1]) : '')
    if (eventId) return { kind: 'event', id: eventId }
    return null
  }

  const target = el || this
  if (!target || !notif) return
  const row = typeof notif === 'string' ? { href: notif } : notif
  const href = _str(row.href)
  // Cross-org target — divert BEFORE the in-place attempt: selectPageItem's
  // loaded-row lookup + SDK fetch are current-org scoped, so for a foreign
  // entity it can only fail (a wasted round trip) or, worse, surface a
  // foreign record over the wrong org's chrome. A notification click must
  // NEVER silently redirect (2026-08-06 per Nika "should open pageSlot or
  // modal - not redirect - same for all notifications"): the cross-org case
  // opens the house confirm MODAL naming the target org, and only an
  // explicit confirm runs the full-load org transition (correct by
  // construction — bootShell F1 boots the URL's org pre-render). Guarded so
  // same-org hrefs keep the existing in-place path byte-identically; without
  // the confirmDialog function (canvas standalone) or a window, fall through
  // to the existing resolution path rather than dead-ending the click.
  if (_isCrossOrgNotificationHref(href, target.state?.root)) {
    const orgSlug = getWorkspacePrefixSlug(href)
    const canConfirm = typeof target.context?.functions?.confirmDialog === 'function'
    if (canConfirm) {
      target.call('confirmDialog', {
        title: target.call('polyglot', 'shell.notifications.otherOrg.title'),
        message: target
          .call('polyglot', 'shell.notifications.otherOrg.message')
          .replace('{org}', orgSlug),
        confirmText: target.call('polyglot', 'shell.notifications.otherOrg.confirm'),
        icon: 'arrowRight',
        onConfirm: () => _hardLoadCrossOrgHref(target, href)
      })
      return
    }
    if (_hardLoadCrossOrgHref(target, href)) return
  }
  const resolved = _resolveNotificationTarget(row)
  if (resolved && typeof target.context?.functions?.selectPageItem === 'function') {
    try {
      const res = await target.call('selectPageItem', resolved.kind, resolved.id)
      if (res && res.ok === true) return
    } catch (err) {
      console.warn('[openNotificationTarget]', err?.message || err)
    }
  }
  // Fallback — entity gone / fetch failed / shell layer absent: hand the href
  // to the shared slot-vs-navigate decision so the click still lands
  // somewhere honest (e.g. /ticket/<id>'s full page shows its own
  // "not in your organization" state).
  if (href) openInSlot.call(target, href, target)
}
